import { Subrecipe } from '../services/subrecipes/subrecipesService';
import { CostPriceAnalysis } from '../services/costPrice/costPriceService';

/**
 * Every subrecipe a cost-price recipe REACHES — the ones referenced directly by a cost
 * price analysis plus, transitively, the ones nested inside those. This is reachability,
 * not the flat set of all subrecipes: a prep that no recipe uses (directly or nested) is
 * an orphan and is deliberately NOT in the result, so inventory can call it "not set up".
 *
 * `subrecipesById` supplies the nesting edges; a reference to an unknown id (deleted prep)
 * is kept in the set but simply has no children to descend into.
 */
export function computeReferencedSubrecipeIds(
  costPriceList: CostPriceAnalysis[],
  subrecipesById: Map<string, Subrecipe>
): Set<string> {
  const referenced = new Set<string>();
  const queue: string[] = [];
  const visit = (id: string | undefined) => {
    if (!id || referenced.has(id)) return;
    referenced.add(id);
    queue.push(id);
  };
  for (const cpa of costPriceList) {
    for (const ing of cpa.ingredients) visit(ing.subrecipeId);
  }
  while (queue.length > 0) {
    const sub = subrecipesById.get(queue.shift()!);
    if (!sub) continue;
    for (const ing of sub.ingredients) visit(ing.nestedSubrecipeId);
  }
  return referenced;
}

/**
 * Returns the set of subrecipe ids that are ancestors of `targetId`.
 * Used to filter the "Add Subrecipe" pickers so we never offer a candidate
 * whose tree already contains the parent (would create a cycle).
 */
export function computeSubrecipeAncestors(
  subrecipes: Subrecipe[],
  targetId: string | null
): Set<string> {
  if (!targetId) return new Set();
  const childToParents = new Map<string, Set<string>>();
  for (const sub of subrecipes) {
    sub.ingredients.forEach((ing) => {
      if (ing.nestedSubrecipeId) {
        if (!childToParents.has(ing.nestedSubrecipeId)) {
          childToParents.set(ing.nestedSubrecipeId, new Set());
        }
        childToParents.get(ing.nestedSubrecipeId)!.add(sub.id);
      }
    });
  }
  const ancestors = new Set<string>();
  const queue: string[] = [targetId];
  while (queue.length > 0) {
    const node = queue.shift()!;
    const parents = childToParents.get(node);
    if (!parents) continue;
    parents.forEach((p) => {
      if (!ancestors.has(p)) {
        ancestors.add(p);
        queue.push(p);
      }
    });
  }
  return ancestors;
}
